import type { StateCreator } from 'zustand';
import { systemHealthApi, type SystemHealthData, type HealthSubtask, type HealthBug, type HealthFeature } from '../services/systemHealthApi';

export type { SystemHealthData, HealthSubtask, HealthBug, HealthFeature };

export interface SystemHealthSlice {
  healthData: SystemHealthData | null;
  healthLoading: boolean;
  fetchSystemHealth: () => Promise<void>;
  createHealthFeature: (data: Partial<HealthFeature>) => Promise<void>;
  updateHealthFeature: (id: number, data: Partial<HealthFeature>) => Promise<void>;
  deleteHealthFeature: (id: number) => Promise<void>;
  createHealthSubtask: (data: Partial<HealthSubtask>) => Promise<void>;
  updateHealthSubtask: (id: number, data: Partial<HealthSubtask>) => Promise<void>;
  deleteHealthSubtask: (id: number) => Promise<void>;
  createHealthBug: (data: Partial<HealthBug>) => Promise<void>;
  updateHealthBug: (id: number, data: Partial<HealthBug>) => Promise<void>;
  deleteHealthBug: (id: number) => Promise<void>;
  seedSystemHealth: () => Promise<void>;
}

export const createSystemHealthSlice: StateCreator<SystemHealthSlice> = (set, get) => ({
  healthData: null,
  healthLoading: false,

  fetchSystemHealth: async () => {
    set({ healthLoading: true });
    try {
      const data = await systemHealthApi.getSummary();
      set({ healthData: data, healthLoading: false });
    } catch {
      set({ healthLoading: false });
    }
  },

  createHealthFeature: async (data) => {
    await systemHealthApi.createFeature(data);
    await get().fetchSystemHealth();
  },

  updateHealthFeature: async (id, data) => {
    await systemHealthApi.updateFeature(id, data);
    await get().fetchSystemHealth();
  },

  deleteHealthFeature: async (id) => {
    await systemHealthApi.deleteFeature(id);
    await get().fetchSystemHealth();
  },

  createHealthSubtask: async (data) => {
    await systemHealthApi.createSubtask(data);
    await get().fetchSystemHealth();
  },

  updateHealthSubtask: async (id, data) => {
    await systemHealthApi.updateSubtask(id, data);
    await get().fetchSystemHealth();
  },

  deleteHealthSubtask: async (id) => {
    await systemHealthApi.deleteSubtask(id);
    await get().fetchSystemHealth();
  },

  createHealthBug: async (data) => {
    await systemHealthApi.createBug(data);
    await get().fetchSystemHealth();
  },

  updateHealthBug: async (id, data) => {
    await systemHealthApi.updateBug(id, data);
    await get().fetchSystemHealth();
  },

  deleteHealthBug: async (id) => {
    await systemHealthApi.deleteBug(id);
    await get().fetchSystemHealth();
  },

  seedSystemHealth: async () => {
    await systemHealthApi.seed();
    await get().fetchSystemHealth();
  },
});
